import { HttpClient, HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http'
import { inject, Injectable, signal } from '@angular/core'
import { Observable, Subscriber } from 'rxjs'
import { environment } from '../../../environments/environment'

interface PendingWrite {
  req: HttpRequest<unknown>
  next: HttpHandlerFn
  subscriber: Subscriber<HttpEvent<unknown>>
  error: HttpErrorResponse
}

/**
 * Single-active-device lease (backend: 423 ANOTHER_DEVICE_ACTIVE).
 *
 * Multiple devices can be logged into the same account, but only one device at
 * a time may use the dashboard — the backend gates reads and writes alike.
 * When the API rejects a request because another device holds the lease, the
 * interceptor parks the request here and we show a take-over dialog.
 * Confirming claims the lease (POST /auth/device/claim) and replays every
 * parked request; dismissing fails them with the original error so normal
 * error handling (toast) kicks in.
 */
@Injectable({ providedIn: 'root' })
export class ActiveDeviceService {
  private http = inject(HttpClient)

  dialogVisible = signal(false)
  claiming = signal(false)

  private pending: PendingWrite[] = []

  holdRejectedWrite(req: HttpRequest<unknown>, next: HttpHandlerFn, error: HttpErrorResponse): Observable<HttpEvent<unknown>> {
    return new Observable(subscriber => {
      this.pending.push({ req, next, subscriber, error })
      this.dialogVisible.set(true)
    })
  }

  takeOver(): void {
    if (this.claiming()) return
    this.claiming.set(true)

    this.http.post(`${environment.baseUrl}/auth/device/claim`, {}).subscribe({
      next: () => {
        this.claiming.set(false)
        this.dialogVisible.set(false)
        const toReplay = this.pending.splice(0)
        toReplay.forEach(({ req, next, subscriber }) => next(req).subscribe(subscriber))
      },
      error: () => {
        this.claiming.set(false)
        this.dialogVisible.set(false)
        this.failPending()
      },
    })
  }

  dismiss(): void {
    if (this.claiming()) return
    this.dialogVisible.set(false)
    this.failPending()
  }

  private failPending(): void {
    const toFail = this.pending.splice(0)
    toFail.forEach(({ subscriber, error }) => subscriber.error(error))
  }
}
